import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { PaperPlaneTilt, MapPin, ForkKnife, Storefront, Scissors, Stethoscope, Buildings } from "@phosphor-icons/react";

const INDUSTRY_ICON = {
  restaurant: <ForkKnife size={18} color="#1E3A2F" />,
  salon: <Scissors size={18} color="#1E3A2F" />,
  clinic: <Stethoscope size={18} color="#1E3A2F" />,
  retail: <Storefront size={18} color="#1E3A2F" />,
  other: <Buildings size={18} color="#1E3A2F" />,
};

export default function Directory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    api.get("/directory").then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  const filtered = filter === "all" ? items : items.filter((i) => i.industry === filter);

  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <header className="glass fixed top-0 left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#1E3A2F] flex items-center justify-center">
              <PaperPlaneTilt size={18} color="#fff" weight="fill" />
            </div>
            <span className="font-heading font-extrabold text-lg">BuzzerAI</span>
          </Link>
          <div className="text-sm">
            <Link to="/pricing" className="btn-primary text-sm">Join the wall</Link>
          </div>
        </div>
      </header>

      <section className="pt-40 pb-10 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <div className="overline mb-3">Built with BuzzerAI</div>
          <h1 className="text-4xl sm:text-5xl mb-4">The operators <span className="text-[#D96C5B]">seating their guests</span> with us.</h1>
          <p className="text-[#5C6B61] text-lg max-w-2xl mx-auto">Real restaurants, salons, clinics and shops. Every one opted in themselves — nobody paid for a spot on this wall.</p>
        </div>
      </section>

      <section className="px-6 pb-8">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-2 justify-center">
          {[["all", "All"], ["restaurant", "Restaurants"], ["salon", "Salons"], ["clinic", "Clinics"], ["retail", "Retail"], ["other", "Other"]].map(([v, label]) => (
            <button key={v} onClick={() => setFilter(v)} data-testid={`directory-filter-${v}`}
              className={`px-4 py-1.5 rounded-full text-sm transition-[background-color] duration-200 ${filter === v ? "bg-[#1E3A2F] text-white" : "bg-white border border-[#E1DFD5] text-[#121914] hover:bg-[#E6E2D6]"}`}>
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="max-w-5xl mx-auto">
          {loading && <div className="text-center text-[#5C6B61]">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="paper-card p-12 text-center">
              <div className="w-14 h-14 rounded-full bg-[#E6E2D6] mx-auto flex items-center justify-center mb-3">
                <Buildings size={22} color="#1E3A2F" />
              </div>
              <h3 className="text-2xl mb-2">Be the first name on this wall.</h3>
              <p className="text-[#5C6B61] max-w-md mx-auto mb-6">The directory is opt-in — we don't publish anyone without permission. Toggle "list us publicly" in your Settings.</p>
              <Link to="/signup" className="btn-primary inline-flex">Sign up free</Link>
            </div>
          )}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((b, i) => (
              <div key={b.id} className="paper-card p-6 fade-up hover:-translate-y-1 transition-transform" style={{ animationDelay: `${i * 40}ms` }}>
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-lg bg-[#E6E2D6] flex items-center justify-center">{INDUSTRY_ICON[b.industry] || INDUSTRY_ICON.other}</div>
                  <span className="tag tag-notified capitalize">{b.industry}</span>
                </div>
                <h3 className="text-xl font-heading font-extrabold">{b.business_name}</h3>
                {b.directory_city && (
                  <div className="flex items-center gap-1 text-xs text-[#5C6B61] mt-1"><MapPin size={12} /><span>{b.directory_city}</span></div>
                )}
                <p className="text-sm text-[#5C6B61] mt-3 leading-relaxed">{b.directory_blurb || "Serving guests with BuzzerAI."}</p>
                <Link to={`/join/${b.slug}`} className="inline-block mt-4 text-sm text-[#1E3A2F] hover:underline" data-testid={`directory-visit-${b.slug}`}>Visit their join page →</Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-[#1E3A2F] text-white px-6 py-14">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-white/70 mb-4">Want to appear here? Toggle "list us publicly" in Settings after you sign up.</p>
          <Link to="/signup" className="btn-primary">Start free</Link>
        </div>
      </footer>
    </div>
  );
}
